import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { useAuth } from '../../auth/AuthProvider';
import { AuthCard } from '../../components/AuthCard';
import { errorMessage } from '../../api/client';

/** First-run setup: only reachable while no user exists. The registrant becomes super admin. */
export function RegisterPage() {
  const { status, needsSetup, refreshSetupStatus, register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (needsSetup === null && status !== 'loading') {
      setStatusError(null);
      refreshSetupStatus().catch((e) => setStatusError(errorMessage(e, 'Could not reach the server')));
    }
  }, [needsSetup, status, refreshSetupStatus, attempt]);

  if (status === 'authenticated') return <Navigate to="/" replace />;
  if (needsSetup === false) return <Navigate to="/login" replace />;
  if (statusError) {
    return (
      <AuthCard title="Set up your CRM">
        <Alert type="error" showIcon title="Could not reach the server" description={statusError} action={<Button size="small" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>} />
      </AuthCard>
    );
  }
  if (needsSetup === null) {
    return (
      <div className="auth-screen">
        <Spin size="large" />
      </div>
    );
  }

  const onFinish = async (values: { name: string; email: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      await register(values.name, values.email, values.password);
      navigate('/', { replace: true });
    } catch (e) {
      setError(errorMessage(e, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="Set up your CRM"
      subtitle="Create the first account. It becomes the super admin and can invite the rest of the team."
    >
      {error && <Alert type="error" title={error} style={{ marginBottom: 16 }} showIcon />}
      <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item name="name" label="Your name" rules={[{ required: true }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input autoComplete="email" />
        </Form.Item>
        <Form.Item name="password" label="Password" rules={[{ required: true, min: 10, message: 'At least 10 characters' }]}>
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading}>
          Create super admin account
        </Button>
      </Form>
    </AuthCard>
  );
}
